import { useMemo, useState, type ReactNode } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { BoundedApiError, type ModulePackage, type PackageDescription, type Parameter, type PolicyBundle } from '@bounded/api-client';
import { MODULE_LIMITS, installPackage, moduleUsage, packageModule, switchCalls, type ParameterChange } from '@bounded/graph-core/module-package';
import { api } from '../../api/client';
import { q } from '../../api/queries';
import { useDraft, type SaveState } from '../../lib/draft';
import { addModuleCall, validate } from '../../lib/graph-edit';
import type { AnyGraph } from '../../lib/graph';
import { downloadJson } from '../../components/policy/source';
import { PublishRail } from '../../components/policy/publish-rail';
import { ErrorNotice, Provenance } from '@bounded/ui/state';
import { Button, Empty, Facts, Field, Input, Mono, Panel, SectionHeader, Skeleton } from '@bounded/ui/primitives';
import { pickLocalized, useLocale, useT } from '../../i18n';

export const Route = createFileRoute('/_app/projects/$projectId/modules')({ component: ModulesPage });

type Apply = (next: PolicyBundle) => void;
/** Local failures (e.g. the 32-module limit) are shown with the same explainer as API errors. */
const localError = (error: unknown) => error instanceof Error && /^MODULE_[A-Z_]+$/.test(error.message) ? new BoundedApiError(400, error.message, null) : error;

function ModulesPage() {
  const t = useT();
  const { projectId } = Route.useParams();
  const draft = useQuery(q.draft(projectId));
  if (draft.error instanceof BoundedApiError && draft.error.code === 'DRAFT_NOT_FOUND') {
    return <Panel><Empty title={t.policy.chooseTitle} action={<Link to="/projects/$projectId/policy" params={{ projectId }} className="text-boundary hover:underline">{t.rollout.choosePolicy}</Link>}>{t.policy.chooseDescription}</Empty></Panel>;
  }
  if (draft.error) return <ErrorNotice error={draft.error} onRetry={() => void draft.refetch()} />;
  if (!draft.data) return <Skeleton className="h-96" />;
  return <ModulesEditor projectId={projectId} />;
}

function ModulesEditor({ projectId }: { projectId: string }) {
  const t = useT(); const m = t.modules;
  const { bundle, base, save, edit, reload, defaultsRevision } = useDraft(projectId);
  const [notice, setNotice] = useState<ReactNode>(null);
  if (!bundle) return <Skeleton className="h-96" />;
  // Same rule as the graph editor: edits that fail local checks are kept but not sent.
  const apply: Apply = (next) => { setNotice(null); edit(next, validate(next.graph as AnyGraph, next.modules).length === 0); };

  return (
    <div className="grid gap-6">
      <SectionHeader title={m.title} description={m.description} />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
        <div className="grid min-w-0 content-start gap-6">
          {/* Results of actions on installed modules appear right above that list. */}
          {notice && <div role="status" className="rounded-[var(--radius-control)] border border-rule bg-surface px-3 py-2 text-sm">{notice}</div>}
          <Installed projectId={projectId} bundle={bundle} apply={apply} setNotice={setNotice} />
          <Import bundle={bundle} apply={apply} />
          <Export bundle={bundle} saveState={save.state} />
        </div>
        <PublishRail projectId={projectId} save={save} base={base} defaultsRevision={defaultsRevision} onReload={reload} />
      </div>
    </div>
  );
}

function Installed({ projectId, bundle, apply, setNotice }: { projectId: string; bundle: PolicyBundle; apply: Apply; setNotice: (n: ReactNode) => void }) {
  const t = useT(); const m = t.modules;
  const usage = useMemo(() => moduleUsage(bundle), [bundle]);
  const [switching, setSwitching] = useState<{ from: string; to: string } | null>(null);
  const entries = Object.entries(bundle.modules).sort(([, a], [, b]) => a.graph_id.localeCompare(b.graph_id) || a.version.localeCompare(b.version));
  const label = (hash: string) => bundle.modules[hash] ? `${bundle.modules[hash].graph_id} ${bundle.modules[hash].version}` : hash;
  const exportOne = useMutation({
    mutationFn: async (hash: string) => { const mod = bundle.modules[hash]; downloadJson(`${mod.graph_id}-${mod.version}.module.json`, await packageModule(mod, bundle.modules)); },
  });

  return (
    <Panel title={m.installed} actions={<span className="text-sm text-graphite tabular-nums">{m.count(entries.length, MODULE_LIMITS.modules)}</span>}>
      {!entries.length ? <Empty title={m.installedEmpty}>{m.installedEmptyBody}</Empty> : (
        <ul>{entries.map(([hash, mod]) => {
          const use = usage[hash];
          const unused = !use.calls.length && !use.requiredBy.length;
          const uses = [...new Set(Object.values(mod.nodes).flatMap((n) => (n as { op: string; config: { module_hash?: string } }).op === 'module.call' ? [(n as { config: { module_hash: string } }).config.module_hash] : []))];
          // Other installed versions of the same module this one's calls could move to.
          const alternatives = entries.filter(([other, o]) => other !== hash && o.graph_id === mod.graph_id);
          return (
            <li key={hash} className="grid gap-3 border-b border-rule px-4 py-3 last:border-b-0">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="grid min-w-0 gap-0.5">
                  <p><span className="font-medium">{mod.graph_id}</span> <span className="text-graphite">{mod.version}</span> <span className="ml-2 text-sm text-graphite">{m.settings(Object.keys(mod.parameters).length)}</span></p>
                  <Mono value={hash} copy className="text-graphite" />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => { const r = addModuleCall(bundle, hash); apply(r.bundle);
                    setNotice(<>{m.added(<code>{r.id}</code>)} {!r.joined && t.editor.moduleNotJoined} <Link to="/projects/$projectId/graph" params={{ projectId }} className="text-boundary hover:underline">{m.openGraph}</Link></>); }}>{m.addToGraph}</Button>
                  <Button size="sm" disabled={exportOne.isPending} onClick={() => exportOne.mutate(hash)}>{m.export}</Button>
                  <Button size="sm" variant="ghost" disabled={!unused} title={unused ? undefined : m.removeTitle}
                    onClick={() => { const next = structuredClone(bundle); delete next.modules[hash]; apply(next); }}>{m.remove}</Button>
                </div>
              </div>
              <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[max-content_minmax(0,1fr)]">
                <dt className="text-graphite">{m.calledBy}</dt>
                <dd>{use.calls.length ? use.calls.map((id, i) => <span key={id}>{i > 0 && ', '}<code>{id}</code></span>) : <span className="text-graphite">{m.unused}</span>}</dd>
                {use.requiredBy.length > 0 && <><dt className="text-graphite">{m.requiredBy}</dt><dd>{use.requiredBy.map(label).join(', ')}</dd></>}
                {uses.length > 0 && <><dt className="text-graphite">{m.uses}</dt><dd>{uses.map(label).join(', ')}</dd></>}
              </dl>
              {use.calls.length > 0 && alternatives.length > 0 && (switching?.from === hash
                ? <SwitchConfirm bundle={bundle} from={hash} to={switching.to} label={label} onCancel={() => setSwitching(null)} onConfirm={(next) => { setSwitching(null); apply(next); }} />
                : <div className="flex flex-wrap gap-2">{alternatives.map(([to, o]) => <Button key={to} size="sm" onClick={() => setSwitching({ from: hash, to })}>{m.switchTo(o.version)}</Button>)}</div>)}
            </li>
          );
        })}</ul>
      )}
      {exportOne.error && <div className="p-4"><ErrorNotice error={localError(exportOne.error)} /></div>}
    </Panel>
  );
}

/** Version switch with the parameter migration spelled out before anything changes. */
function SwitchConfirm({ bundle, from, to, label, onConfirm, onCancel }: { bundle: PolicyBundle; from: string; to: string; label: (h: string) => string; onConfirm: Apply; onCancel: () => void }) {
  const t = useT(); const m = t.modules;
  const result = useMemo(() => switchCalls(bundle, from, to), [bundle, from, to]);
  const calls = moduleUsage(bundle)[from].calls.length;
  return (
    <div className="grid max-w-3xl gap-3 rounded-[var(--radius-panel)] border border-rule-strong p-4">
      <p className="font-semibold">{m.switchTitle(label(from), label(to))}</p>
      <p className="text-graphite">{m.switchBody(calls)}</p>
      <Changes changes={result.changes} />
      <div className="flex gap-2"><Button variant="primary" size="sm" onClick={() => onConfirm(result.bundle)}>{m.confirmSwitch}</Button><Button variant="ghost" size="sm" onClick={onCancel}>{m.cancel}</Button></div>
    </div>
  );
}
function Changes({ changes }: { changes: ParameterChange[] }) {
  const t = useT(); const m = t.modules;
  if (!changes.length) return <p className="text-sm">{m.switchKeepsAll}</p>;
  return (
    <div className="grid gap-1 text-sm">
      <p>{m.switchDropped}</p>
      <ul className="grid gap-0.5">{changes.map((c) => <li key={`${c.call}/${c.parameter}`}><code>{c.call}</code> <code>{c.parameter}</code> <span className="text-graphite">{m.dropReason[c.reason]}</span></li>)}</ul>
    </div>
  );
}

function Import({ bundle, apply }: { bundle: PolicyBundle; apply: Apply }) {
  const t = useT(); const m = t.modules;
  const [text, setText] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const inspect = useMutation({ mutationFn: (value: unknown) => api.inspectModule(value) });
  // What the last install did, shown in this panel where it was done.
  const [result, setResult] = useState<ReactNode>(null);
  const check = (raw: string) => {
    setProblem(null); setResult(null); inspect.reset();
    if (new TextEncoder().encode(raw).byteLength > MODULE_LIMITS.bytes) return setProblem(m.tooLarge);
    let value: unknown; try { value = JSON.parse(raw); } catch { return setProblem(m.invalidJson); }
    inspect.mutate(value);
  };
  const done = (next: PolicyBundle, message: ReactNode) => { apply(next); setText(''); inspect.reset(); setResult(message); };

  return (
    <Panel title={m.importTitle} description={m.importDescription}>
      <div className="grid gap-4 p-4">
        {result && <div role="status" className="rounded-[var(--radius-control)] border border-rule bg-paper px-3 py-2 text-sm">{result}</div>}
        {!inspect.data && <>
          <div>
            <label className="inline-flex h-8 cursor-pointer items-center rounded-[var(--radius-control)] border border-rule-strong bg-surface px-3 hover:bg-sunken focus-within:outline-2 focus-within:outline-boundary">
              {m.chooseFile}
              <input type="file" accept="application/json,.json" aria-label={m.chooseFile} className="sr-only"
                onChange={(e) => { const file = e.target.files?.[0]; if (file) void file.text().then(check); e.target.value = ''; }} />
            </label>
          </div>
          <Field label={m.paste} htmlFor="module-paste">
            <textarea id="module-paste" rows={4} spellCheck={false} value={text} onChange={(e) => setText(e.target.value)}
              className="w-full rounded-[var(--radius-control)] border border-rule-strong bg-surface px-2.5 py-1.5 font-mono text-xs leading-relaxed" />
          </Field>
          <div><Button disabled={!text.trim() || inspect.isPending} onClick={() => check(text)}>{inspect.isPending ? m.checking : m.check}</Button></div>
        </>}
        {problem && <p role="alert" className="text-deny">{problem}</p>}
        {inspect.error && <ErrorNotice error={inspect.error} />}
        {inspect.data && <Preview bundle={bundle} pkg={inspect.data.package} description={inspect.data.description} onCancel={() => inspect.reset()} onDone={done} />}
      </div>
    </Panel>
  );
}

/** Install preview: what the module is, what it can do and send, and how it relates to what the policy already has. */
function Preview({ bundle, pkg, description: d, onCancel, onDone }: { bundle: PolicyBundle; pkg: ModulePackage; description: PackageDescription; onCancel: () => void; onDone: (next: PolicyBundle, message: ReactNode) => void }) {
  const t = useT(); const m = t.modules;
  const { locale } = useLocale();
  const [error, setError] = useState<unknown>(null);
  const usage = moduleUsage(bundle);
  const already = Object.hasOwn(bundle.modules, pkg.entry);
  const older = Object.entries(bundle.modules).filter(([hash, g]) => hash !== pkg.entry && g.graph_id === d.manifest.name);
  const attempt = (fn: () => void) => { try { setError(null); fn(); } catch (e) { setError(localError(e)); } };
  const installed = () => installPackage(bundle, pkg);
  const params = Object.entries(d.parameters as Record<string, Parameter>);

  return (
    <div className="grid gap-4">
      <div className="grid gap-0.5">
        <p className="text-lg font-semibold">{d.manifest.title ?? d.manifest.name} <span className="font-normal text-graphite">{d.manifest.version}</span></p>
        {d.manifest.author && <p className="text-sm text-graphite">{m.by(d.manifest.author)}</p>}
        {d.manifest.description && <p className="max-w-[72ch]">{d.manifest.description}</p>}
      </div>
      <Facts items={[
        [m.name, <code key="n">{d.manifest.name}</code>],
        [m.fingerprint, <Mono key="h" value={d.hash} copy />],
        [m.steps, d.nodes],
        [m.grants, d.operations.length ? d.operations.map((o) => <code key={o} className="mr-2">{o}</code>) : m.none],
        [m.models, d.models.length ? <ul key="m" className="grid gap-0.5">{d.models.map((x) => <li key={x.connection}><code>{x.connection}</code>{x.models.length > 0 && <span className="text-graphite"> {x.models.join(', ')}</span>}</li>)}</ul> : <span key="m" className="text-graphite">{m.noModels}</span>],
        ...(d.context.length ? [[m.sends, <ul key="c" className="grid gap-0.5">{d.context.map((c) => <li key={c}><code>{c}</code></li>)}</ul>]] as [string, ReactNode][] : []),
        [m.adds, params.length ? <ul key="p" className="grid gap-0.5">{params.map(([id, p]) => <li key={id}>{pickLocalized({ label: p.label }, p.i18n, locale).label} <span className="text-graphite"><code>{JSON.stringify(p.default)}</code></span></li>)}</ul> : m.none],
        [m.includes, d.dependencies.length ? d.dependencies.map((x) => `${x.name} ${x.version}`).join(', ') : m.includesNone],
      ]} />
      {already ? <p className="text-graphite">{m.already}</p> : older.map(([hash, g]) => (
        <div key={hash} className="grid gap-2">
          <p className="text-graphite">{m.replaces(g.version, usage[hash].calls.length)}</p>
          {/* What switching would do to each call's settings, shown before the button is pressed. */}
          {usage[hash].calls.length > 0 && <SwitchPreview bundle={bundle} pkg={pkg} from={hash} />}
        </div>
      ))}
      {error ? <ErrorNotice error={error} /> : null}
      <div className="flex flex-wrap gap-2">
        {!already && <Button variant="primary" onClick={() => attempt(() => onDone(installed(), t.modules.added(<code>{d.manifest.name} {d.manifest.version}</code>)))}>{m.install}</Button>}
        {!already && !older.some(([hash]) => usage[hash].calls.length) && (
          <Button onClick={() => attempt(() => { const r = addModuleCall(installed(), pkg.entry); onDone(r.bundle, <>{m.added(<code>{r.id}</code>)} {!r.joined && t.editor.moduleNotJoined}</>); })}>{m.installAndCall}</Button>
        )}
        {!already && older.filter(([hash]) => usage[hash].calls.length).map(([hash, g]) => (
          <Button key={hash} onClick={() => attempt(() => { const r = switchCalls(installed(), hash, pkg.entry); onDone(r.bundle, <Changes changes={r.changes} />); })}>{m.installAndSwitch(g.version)}</Button>
        ))}
        <Button variant="ghost" onClick={onCancel}>{m.cancel}</Button>
      </div>
    </div>
  );
}

function SwitchPreview({ bundle, pkg, from }: { bundle: PolicyBundle; pkg: ModulePackage; from: string }) {
  const changes = useMemo(() => { try { return switchCalls(installPackage(bundle, pkg), from, pkg.entry).changes; } catch { return null; } }, [bundle, pkg, from]);
  return changes ? <Changes changes={changes} /> : null;
}

function Export({ bundle, saveState }: { bundle: PolicyBundle; saveState: SaveState['state'] }) {
  const ready = saveState === 'saved';
  const t = useT(); const m = t.modules;
  const [name, setName] = useState(bundle.graph.graph_id);
  const [version, setVersion] = useState(bundle.graph.version);
  const [title, setTitle] = useState(''); const [description, setDescription] = useState(''); const [author, setAuthor] = useState('');
  const valid = /^[a-z][a-z0-9_-]{0,95}$/.test(name) && !!version.trim() && version.trim().length <= 64;
  const download = useMutation({
    mutationFn: async () => {
      const entry = { ...structuredClone(bundle.graph), graph_id: name, version: version.trim() };
      downloadJson(`${name}-${version.trim()}.module.json`, await packageModule(entry, bundle.modules, { title, description, author }));
    },
  });
  return (
    <Panel title={m.exportTitle} description={m.exportDescription}>
      <form className="grid gap-4 p-4" onSubmit={(e) => { e.preventDefault(); if (valid && ready) download.mutate(); }}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={m.name} htmlFor="export-name" hint={m.nameHint}><Input id="export-name" className="font-mono" value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label={m.version} htmlFor="export-version" hint={m.versionHint}><Input id="export-version" value={version} onChange={(e) => setVersion(e.target.value)} /></Field>
          <Field label={m.titleField} htmlFor="export-title"><Input id="export-title" maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
          <Field label={m.author} htmlFor="export-author"><Input id="export-author" maxLength={120} value={author} onChange={(e) => setAuthor(e.target.value)} /></Field>
        </div>
        <Field label={m.descriptionField} htmlFor="export-description">
          <textarea id="export-description" rows={2} maxLength={1000} value={description} onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded-[var(--radius-control)] border border-rule-strong bg-surface px-2.5 py-1.5" />
        </Field>
        {download.error && <ErrorNotice error={localError(download.error)} />}
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={!valid || !ready || download.isPending}>{m.download}</Button>
          {!ready && <Provenance>{saveState === 'invalid' || saveState === 'error' || saveState === 'conflict' ? m.exportInvalid : m.exportSaving}</Provenance>}
        </div>
      </form>
    </Panel>
  );
}
